import type { ListSnapshot, Neighbor, Position, Row } from './types';

export type BoundaryNeeds = {
    needPrevPage: boolean;
    needNextPage: boolean;
};

export const computeBoundaryNeeds = (
    currentIdx: number,
    rows: Row[],
    offset: number,
    limit: number,
    totalCount: number
): BoundaryNeeds => {
    const isFirstInPage = currentIdx === 0;
    const isLastInPage = currentIdx >= 0 && currentIdx === rows.length - 1;
    return {
        needPrevPage: isFirstInPage && offset > 0,
        needNextPage: isLastInPage && offset + limit < totalCount
    };
};

type ComputeNeighborsParams<TQuery> = {
    rows: Row[];
    currentIdx: number;
    offset: number;
    limit: number;
    totalCount: number;
    snapshot: ListSnapshot<TQuery> | null;
    prevPageRows: Row[];
    nextPageRows: Row[];
};

export const computeNeighbors = <TQuery>({
    rows,
    currentIdx,
    offset,
    limit,
    totalCount,
    snapshot,
    prevPageRows,
    nextPageRows
}: ComputeNeighborsParams<TQuery>): {
    prev: Neighbor;
    next: Neighbor;
    position: Position | undefined;
} => {
    const buildSnapshot = (offsetOverride?: number): ListSnapshot | undefined => {
        if (!snapshot) return undefined;
        if (offsetOverride === undefined) return snapshot as ListSnapshot;
        return {
            ...snapshot,
            listQuery: {
                ...(snapshot.listQuery as object),
                offset: String(offsetOverride)
            }
        } as ListSnapshot;
    };

    const { needPrevPage, needNextPage } = computeBoundaryNeeds(
        currentIdx,
        rows,
        offset,
        limit,
        totalCount
    );

    let prev: Neighbor;
    if (currentIdx > 0) {
        const row = rows[currentIdx - 1];
        const fs = buildSnapshot();
        if (fs) prev = { ...row, forwardSnapshot: fs };
    } else if (needPrevPage && prevPageRows.length > 0) {
        const row = prevPageRows[prevPageRows.length - 1];
        const fs = buildSnapshot(offset - limit);
        if (fs) prev = { ...row, forwardSnapshot: fs };
    }

    let next: Neighbor;
    if (currentIdx >= 0 && currentIdx < rows.length - 1) {
        const row = rows[currentIdx + 1];
        const fs = buildSnapshot();
        if (fs) next = { ...row, forwardSnapshot: fs };
    } else if (needNextPage && nextPageRows.length > 0) {
        const row = nextPageRows[0];
        const fs = buildSnapshot(offset + limit);
        if (fs) next = { ...row, forwardSnapshot: fs };
    }

    const position =
        currentIdx >= 0 && totalCount > 0
            ? { current: offset + currentIdx + 1, total: totalCount }
            : undefined;

    return { prev, next, position };
};
